/**
 * Minimoog - Emulation du synthétiseur Minimoog Model D
 *
 * 3 oscillateurs, filtre ladder 24dB, 2 enveloppes
 */

import * as Tone from 'tone';

export class Minimoog {
    constructor(audioEngine, options = {}) {
        this.audioEngine = audioEngine;
        this.performance = options.performance || { lowPower: false };
        this.currentNote = null;
        this.glideTime = 0;

        // État power (pour économiser les ressources)
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut
        this.params = {
            // Oscillateur 1
            osc1Waveform: 'sawtooth',
            osc1Octave: 0,
            osc1Tune: 0,
            osc1Level: 0.8,
            // Oscillateur 2
            osc2Waveform: 'sawtooth',
            osc2Octave: 0,
            osc2Tune: 0,
            osc2Level: 0.6,
            // Oscillateur 3
            osc3Waveform: 'sawtooth',
            osc3Octave: -1,
            osc3Tune: 0,
            osc3Level: 0.4,
            // Noise
            noiseLevel: 0,
            // Filter
            cutoff: 2000,
            resonance: 1,
            filterEnvAmount: 0.5,
            filterKeyTrack: 0.5,
            // Filter Envelope
            filterAttack: 0.01,
            filterDecay: 0.3,
            filterSustain: 0.4,
            // Amp Envelope
            ampAttack: 0.01,
            ampDecay: 0.2,
            ampSustain: 0.7,
            ampRelease: 0.3,
            // Glide
            glide: 0,
            // Master
            volume: 0.7
        };

        this._createSynth();
    }

    _createSynth() {
        // Créer les 3 oscillateurs
        this.osc1 = new Tone.Oscillator({
            type: this.params.osc1Waveform,
            frequency: 440
        });

        this.osc2 = new Tone.Oscillator({
            type: this.params.osc2Waveform,
            frequency: 440
        });

        this.osc3 = new Tone.Oscillator({
            type: this.params.osc3Waveform,
            frequency: 220  // -1 octave
        });

        // Gains individuels pour mixer
        this.osc1Gain = new Tone.Gain(this.params.osc1Level);
        this.osc2Gain = new Tone.Gain(this.params.osc2Level);
        this.osc3Gain = new Tone.Gain(this.params.osc3Level);

        // Noise
        this.noise = new Tone.Noise('white');
        this.noiseGain = new Tone.Gain(this.params.noiseLevel);

        // Mixer
        this.mixer = new Tone.Gain(1);

        // Connecter oscillateurs au mixer
        this.osc1.connect(this.osc1Gain);
        this.osc2.connect(this.osc2Gain);
        this.osc3.connect(this.osc3Gain);
        this.noise.connect(this.noiseGain);

        this.osc1Gain.connect(this.mixer);
        this.osc2Gain.connect(this.mixer);
        this.osc3Gain.connect(this.mixer);
        this.noiseGain.connect(this.mixer);

        // Filtre Ladder 24dB (4 pôles)
        this.filter = new Tone.Filter({
            type: 'lowpass',
            frequency: this.params.cutoff,
            rolloff: -24,
            Q: this.params.resonance
        });

        // Envelope pour le filtre
        this.filterEnv = new Tone.Envelope({
            attack: this.params.filterAttack,
            decay: this.params.filterDecay,
            sustain: this.params.filterSustain,
            release: 0.5
        });

        // Scale pour l'envelope du filtre
        this.filterEnvScale = new Tone.Scale(0, 10000);
        this.filterEnv.connect(this.filterEnvScale);

        // Envelope pour l'amplitude
        this.ampEnv = new Tone.AmplitudeEnvelope({
            attack: this.params.ampAttack,
            decay: this.params.ampDecay,
            sustain: this.params.ampSustain,
            release: this.params.ampRelease
        });

        // Master volume
        this.masterGain = new Tone.Gain(this.params.volume);

        // Connexions
        this.mixer.connect(this.filter);
        this.filter.connect(this.ampEnv);
        this.ampEnv.connect(this.masterGain);

        // Connecter au mixer global
        this.mixerChannel = this.audioEngine.getMixerChannelInput('minimoog');
        if (this.mixerChannel) {
            this.masterGain.connect(this.mixerChannel);
        } else {
            // Fallback: connecter directement au master
            this.masterGain.connect(this.audioEngine.getMasterVolume());
        }

        // Démarrer les oscillateurs (ils seront silencieux jusqu'à triggerAttack)
        this._startOscillators();

        // Par défaut, le volume est à 0 (géré par l'enveloppe)
        this.ampEnv.set({ sustain: 0 });
    }

    /**
     * Démarre les oscillateurs
     */
    _startOscillators() {
        if (this.osc1?.state !== 'started') this.osc1.start();
        if (this.osc2?.state !== 'started') this.osc2.start();
        if (this.osc3?.state !== 'started') this.osc3.start();
        if (this.noise?.state !== 'started') this.noise.start();
    }

    /**
     * Arrête les oscillateurs
     */
    _stopOscillators() {
        if (this.osc1?.state === 'started') this.osc1.stop();
        if (this.osc2?.state === 'started') this.osc2.stop();
        if (this.osc3?.state === 'started') this.osc3.stop();
        if (this.noise?.state === 'started') this.noise.stop();
    }

    /**
     * Calcule la fréquence avec octave et détune
     */
    _calcFrequency(baseFreq, octave, tune) {
        const octaveMultiplier = Math.pow(2, octave);
        const tuneMultiplier = Math.pow(2, tune / 1200);  // tune en cents
        return baseFreq * octaveMultiplier * tuneMultiplier;
    }

    /**
     * Joue une note
     */
    triggerAttack(note, time, velocity = 1) {
        const freq = Tone.Frequency(note).toFrequency();
        const now = time || Tone.now();

        // Glide (portamento)
        const glideTime = this.params.glide;

        // Mettre à jour les fréquences des oscillateurs
        this.osc1.frequency.rampTo(
            this._calcFrequency(freq, this.params.osc1Octave, this.params.osc1Tune),
            glideTime,
            now
        );
        this.osc2.frequency.rampTo(
            this._calcFrequency(freq, this.params.osc2Octave, this.params.osc2Tune),
            glideTime,
            now
        );
        this.osc3.frequency.rampTo(
            this._calcFrequency(freq, this.params.osc3Octave, this.params.osc3Tune),
            glideTime,
            now
        );

        // Keyboard tracking pour le filtre
        const keyTrackAmount = this.params.filterKeyTrack;
        const baseFreqRatio = freq / 261.63;  // Ratio par rapport à C4
        const keyTrackOffset = (baseFreqRatio - 1) * keyTrackAmount * 2000;

        // Calculer la fréquence cible du filtre avec l'envelope
        const filterBase = this.params.cutoff + keyTrackOffset;
        const filterEnvAmount = this.params.filterEnvAmount * 8000;

        // Appliquer l'envelope du filtre manuellement
        const filterPeak = Math.min(filterBase + filterEnvAmount, 18000);
        const filterSustainFreq = filterBase + (filterEnvAmount * this.params.filterSustain);

        this.filter.frequency.cancelScheduledValues(now);
        this.filter.frequency.setValueAtTime(filterBase, now);
        this.filter.frequency.linearRampToValueAtTime(filterPeak, now + this.params.filterAttack);
        this.filter.frequency.linearRampToValueAtTime(filterSustainFreq, now + this.params.filterAttack + this.params.filterDecay);

        // Trigger l'enveloppe d'amplitude
        this.ampEnv.triggerAttack(now, velocity);

        this.currentNote = note;
    }

    /**
     * Relâche la note
     */
    triggerRelease(time) {
        const now = time || Tone.now();
        this.ampEnv.triggerRelease(now);

        // Release du filtre
        this.filter.frequency.linearRampToValueAtTime(
            this.params.cutoff,
            now + this.params.ampRelease
        );
    }

    /**
     * Joue une note avec durée
     */
    triggerAttackRelease(note, duration, time, velocity = 1) {
        const now = time || Tone.now();
        this.triggerAttack(note, now, velocity);

        const releaseTime = now + Tone.Time(duration).toSeconds();
        this.triggerRelease(releaseTime);
    }

    // ========== SETTERS ==========

    // Oscillateur 1
    setOsc1Waveform(waveform) {
        this.params.osc1Waveform = waveform;
        this.osc1.type = waveform;
    }

    setOsc1Octave(octave) {
        this.params.osc1Octave = octave;
    }

    setOsc1Tune(cents) {
        this.params.osc1Tune = cents;
    }

    setOsc1Level(level) {
        this.params.osc1Level = level;
        this.osc1Gain.gain.value = level;
    }

    // Oscillateur 2
    setOsc2Waveform(waveform) {
        this.params.osc2Waveform = waveform;
        this.osc2.type = waveform;
    }

    setOsc2Octave(octave) {
        this.params.osc2Octave = octave;
    }

    setOsc2Tune(cents) {
        this.params.osc2Tune = cents;
    }

    setOsc2Level(level) {
        this.params.osc2Level = level;
        this.osc2Gain.gain.value = level;
    }

    // Oscillateur 3
    setOsc3Waveform(waveform) {
        this.params.osc3Waveform = waveform;
        this.osc3.type = waveform;
    }

    setOsc3Octave(octave) {
        this.params.osc3Octave = octave;
    }

    setOsc3Tune(cents) {
        this.params.osc3Tune = cents;
    }

    setOsc3Level(level) {
        this.params.osc3Level = level;
        this.osc3Gain.gain.value = level;
    }

    // Noise
    setNoiseLevel(level) {
        this.params.noiseLevel = level;
        this.noiseGain.gain.value = level;
    }

    // Filter
    setCutoff(freq) {
        this.params.cutoff = freq;
        this.filter.frequency.value = freq;
    }

    setResonance(q) {
        this.params.resonance = q;
        this.filter.Q.value = q;
    }

    setFilterEnvAmount(amount) {
        this.params.filterEnvAmount = amount;
    }

    setFilterKeyTrack(amount) {
        this.params.filterKeyTrack = amount;
    }

    // Filter Envelope
    setFilterAttack(time) {
        this.params.filterAttack = time;
        this.filterEnv.attack = time;
    }

    setFilterDecay(time) {
        this.params.filterDecay = time;
        this.filterEnv.decay = time;
    }

    setFilterSustain(level) {
        this.params.filterSustain = level;
        this.filterEnv.sustain = level;
    }

    // Amp Envelope
    setAmpAttack(time) {
        this.params.ampAttack = time;
        this.ampEnv.attack = time;
    }

    setAmpDecay(time) {
        this.params.ampDecay = time;
        this.ampEnv.decay = time;
    }

    setAmpSustain(level) {
        this.params.ampSustain = level;
        this.ampEnv.sustain = level;
    }

    setAmpRelease(time) {
        this.params.ampRelease = time;
        this.ampEnv.release = time;
    }

    // Glide
    setGlide(time) {
        this.params.glide = time;
    }

    // Master
    setVolume(level) {
        this.params.volume = level;
        this.masterGain.gain.value = level;
    }

    // ========== GETTERS ==========
    getParams() {
        return { ...this.params };
    }

    setParams(params) {
        Object.entries(params).forEach(([key, value]) => {
            // Convert param name to setter name (e.g., 'cutoff' -> 'setCutoff')
            const setter = `set${key.charAt(0).toUpperCase() + key.slice(1)}`;
            if (typeof this[setter] === 'function') {
                this[setter](value);
            }
        });
    }

    // --- Power ON/OFF ---

    setPower(on) {
        if (on === this.isPowered) return;

        this.isPowered = on;

        if (on) {
            if (this.mixerChannel) {
                this.masterGain.connect(this.mixerChannel);
            }
            this._startOscillators();
        } else {
            this.masterGain.disconnect();
            this._stopOscillators();
        }
    }

    getPower() {
        return this.isPowered;
    }
}
